import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createExternalProvider,
  externalProviderQuery,
  externalProvidersTableQuery,
  removeExternalProvider,
  saveExternalProvider,
  type ExternalProviderFields,
} from '../../api/settingsEaseedeskExternalProviders'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { esc } from './AdamIntopPythonFilesPage'

/**
 * Settings -> Easeedesk -> External service providers, element cu element
 * fata de `easeedesk_settings/external_providers/index.php` + `index-1.js`:
 * tabelul DataTables (nume, email, telefon, Edit/Remove - HTML-ul lui
 * `Firma::externalProvidersActionsForm`), fereastra "New external provider"
 * si "Edit external provider" cu aceleasi trei campuri, intrebarea de swal
 * la Remove si aceleasi mesaje.
 */
const TABLE_ID = 'external-providers'
const ORDER_NAMES = ['name', 'email', 'phone', 'name', 'name']
const EMPTY: ExternalProviderFields = { provider_name: '', provider_email: '', provider_phone: '' }

function columns(): TableColumn[] {
  const text = (key: string): TableColumn['html'] => (row) => esc(String(row[key] ?? ''))
  return [
    { index: 0, title: 'Name', orderable: true, visible: true, className: 'a-left', html: text('name') },
    { index: 1, title: 'Email', orderable: true, visible: true, className: 'a-left', html: text('email') },
    { index: 2, title: 'Phone', orderable: true, visible: true, className: 'a-left', html: text('phone') },
    {
      /* Sablonul "Waiting for Third Party" al furnizorului (7 octombrie 2026);
         gol = cel implicit general. Se leaga din Third party escalation templates. */
      index: 3,
      title: 'Escalation template',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) =>
        `<a href="/app/settings/easeedesk/third_party_templates_settings" class="ep-escalation-template">${
          String(row.escalation_template ?? '') !== '' ? esc(String(row.escalation_template)) : '<span class="text-muted">General (default)</span>'
        }</a>`,
    },
    {
      index: 4,
      /*
       * `externalProvidersModel`, coloana 3: `className: 'center'` si
       * `width: 200`. Fara clasa, cele doua butoane porneau de la marginea din
       * stanga a coloanei, deci grupul statea in alt loc decat capul "Actions"
       * de deasupra lui si decat coloana de actiuni a oricarei alte liste din
       * zona. `.center` e in `public/css/app.css` (`.align-center, .center`).
       */
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) =>
        `<div class="btn-group" role="group" aria-label="...">
          <button type="button" data-provider="${Number(row.ID)}" class="btn btn-sm btn-default edit-provider"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>
          <button type="button" data-provider="${Number(row.ID)}" class="btn btn-sm btn-default remove-provider"><i class="fas fa-times" aria-hidden="true"></i> Remove</button>
        </div>`,
    },
  ]
}

export function ExternalProvidersPage() {
  const client = useQueryClient()
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'asc' }, search: '' })
  const cols = useMemo(() => columns(), [])
  const table = useQuery({
    ...externalProvidersTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'name',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(0)

  if (table.error instanceof ApiError && table.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }

  const redraw = () => client.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'external_providers'] })

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const edit = target.closest('.edit-provider')
    if (edit instanceof HTMLElement) {
      setEditing(Number(edit.dataset.provider ?? 0))
      return
    }
    const remove = target.closest('.remove-provider')
    if (remove instanceof HTMLElement) {
      /* Intrebarea din swal, cu acelasi text. */
      if (!window.confirm('Are you sure?\nYou will not be able to recover this provider!')) return
      void removeExternalProvider(Number(remove.dataset.provider ?? 0))
        .then(() => {
          toast('Provider removed.', 'Success')
          void redraw()
        })
        .catch(() => toast('Something went wrong.', 'Error', 'error'))
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> New external provider
      </button>
      <h4>
        <strong>External</strong> service providers
      </h4>
      <hr />

      <DataTable
        id={TABLE_ID}
        columns={cols}
        rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="An external service provider is a company tickets can be escalated to."
      />

      <ProviderModal
        id="newExternalProviderModal"
        title="New external provider"
        open={creating}
        provider={0}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false)
          toast('New external provider added.', 'Success')
          void redraw()
        }}
      />
      <ProviderModal
        id="editExternalProviderModal"
        title="Edit external provider"
        open={editing > 0}
        provider={editing}
        onClose={() => setEditing(0)}
        onSaved={() => {
          setEditing(0)
          toast('Settings saved.', 'Success')
          void redraw()
        }}
      />
    </div>
  )
}

function ProviderModal({
  id,
  title,
  open,
  provider,
  onClose,
  onSaved,
}: {
  id: string
  title: string
  open: boolean
  provider: number
  onClose(): void
  onSaved(): void
}) {
  const current = useQuery({ ...externalProviderQuery(provider), enabled: open && provider > 0 })
  const [form, setForm] = useState<ExternalProviderFields>(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) {
      setForm(EMPTY)
      return
    }
    if (current.data) {
      setForm({ provider_name: current.data.name, provider_email: current.data.email, provider_phone: current.data.phone })
    }
  }, [open, current.data])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const call = provider > 0 ? saveExternalProvider(provider, form) : createExternalProvider(form)
    void call
      .then(() => {
        setBusy(false)
        onSaved()
      })
      .catch(() => {
        setBusy(false)
        toast('Something went wrong.', 'Error', 'error')
      })
  }

  const field = (key: keyof ExternalProviderFields) => (event: FormEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: event.currentTarget.value })

  return (
    <Modal id={id} labelledBy={`${id}Label`} open={open} onClose={onClose} title={title}>
      {provider > 0 && !current.data ? (
        <Spinner />
      ) : (
        <form method="post" id={provider > 0 ? 'edit-provider-form' : 'new-provider-form'} onSubmit={submit}>
          <input type="hidden" name="provider" value={provider} />
          <div className="form-group">
            <label htmlFor={`${id}-name`} className="control-label">
              Name <span className="required">*</span>
            </label>
            <input type="text" className="form-control input-sm" id={`${id}-name`} name="provider_name" required value={form.provider_name} onChange={field('provider_name')} />
          </div>
          <div className="form-group">
            <label htmlFor={`${id}-email`} className="control-label">
              Email <span className="required">*</span>
            </label>
            <input type="text" className="form-control input-sm" id={`${id}-email`} name="provider_email" required value={form.provider_email} onChange={field('provider_email')} />
          </div>
          <div className="form-group">
            <label htmlFor={`${id}-phone`} className="control-label">
              Phone
            </label>
            <input type="text" className="form-control input-sm" id={`${id}-phone`} name="provider_phone" value={form.provider_phone} onChange={field('provider_phone')} />
          </div>
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {provider > 0 ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
